import flatten from 'lodash/flatten';
import { EntryTypeApi } from '@api/EntryTypeApi';
import { store } from '@store';

import { EntryTypeFieldType, EntryTypeStatus } from 'chaika-enums';
import { getUniqChunkedIds } from '@utils/getUniqChunkedIds';
import { assertIsDefined } from '@utils/assertIsDefined';
import { EntryTypeListItemData, GetEntryTypeListQuery } from 'chaika-frontend-types/frontend-cms';

const customWidthFields = new Set([EntryTypeFieldType.Table]);

class EntryTypeService {
    private isActiveLoaded = false;
    private onLoadNext: boolean = false;

    public async loadByIds(ids: string[], params: { withTranslations?: boolean } = {}): Promise<void> {
        // Не стоит использовать newUniqChunkedIds. Помимо id тут есть еще и версия
        const uniqIds = getUniqChunkedIds(ids);

        if (uniqIds.length > 0) {
            const entryTypes = await Promise.all(uniqIds.map(newIds => EntryTypeApi.getByIds(newIds, params)));

            store.entryTypeList.add(flatten(entryTypes));
        }
    }

    public async load(params?: GetEntryTypeListQuery): Promise<void> {
        const { entryTypes, count } = await EntryTypeApi.getList(params);
        store.entryTypeList.set(entryTypes);
        store.entryTypeList.count = count;
    }

    public async loadNext(): Promise<void> {
        if (!this.onLoadNext && store.entryTypeList.hasNext) {
            this.onLoadNext = true;

            const { entryTypes } = await EntryTypeApi.getList();
            store.entryTypeList.addNext(entryTypes);

            this.onLoadNext = false;
        }
    }

    public async findAndAdd(params: GetEntryTypeListQuery): Promise<EntryTypeListItemData[]> {
        const { entryTypes } = await EntryTypeApi.getList(params);

        store.entryTypeList.addIfNotExist(entryTypes);

        return entryTypes;
    }

    public async loadActive(): Promise<void> {
        if (!this.isActiveLoaded) {
            this.isActiveLoaded = true;
            const { entryTypes } = await EntryTypeApi.getList({ status: EntryTypeStatus.Active });
            store.entryTypeList.set(entryTypes);
        }
    }

    public async loadEntryTypeTemplate(typeId: string, version: number): Promise<void> {
        const { entryTypeList } = store;

        const entryType = entryTypeList.getVersion(typeId, version);
        assertIsDefined(entryType, `Entry type ${typeId} v${version} must be loaded before its template`);

        if (entryType.sections.length === 0) {
            const { sections } = await EntryTypeApi.getVersion(typeId, version);

            entryType.setSections(sections);
        }
    }

    public isCustomWidthField(fieldType: EntryTypeFieldType): boolean {
        return customWidthFields.has(fieldType);
    }
}

export const entryTypeService = new EntryTypeService();
